import { createRootRouteWithContext, Outlet, ErrorComponent } from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query/devtools';
// import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Suspense } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AgentPanelWrapper } from '@/components/organisms/AgentPanelWrapper';
import { useThemeEffect } from '@/stores/uiStore';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';

/**
 * Root Route Layout
 * 
 * Implements Requirements 6.1, 6.2, 6.4, 6.9, 14.1, 14.2:
 * - TanStack Router for type-safe routing
 * - Nested routes with layout composition
 * - Route data preloading with Suspense
 * - Loading states with Suspense boundaries
 * - Error boundaries at root level
 * - Fallback UI when component throws error
 * 
 * This is the root layout that wraps all routes in the application.
 * It provides:
 * - QueryClient context for TanStack Query
 * - Suspense boundary for loading states
 * - Error boundary for catching and displaying errors
 * - DevTools for development (React Query and Router)
 */

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: () => <div>{i18n.t('rootLayout:notFound')}</div>,
  pendingComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground">{i18n.t('common:messages.loading')}</p>
      </div>
    </div>
  ),
});

function RootComponent() {
  const { t } = useTranslation();
  useThemeEffect();

  return (
    <ErrorBoundary level="root">
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground">{i18n.t('common:messages.loading')}</p>
            </div>
          </div>
        }
      >
        <Outlet />
        <AgentPanelWrapper />
      </Suspense>
      {/* Development tools - only visible in development */}
      {/* {import.meta.env.DEV && (
        <>
          <ReactQueryDevtools initialIsOpen={false} />
          <TanStackRouterDevtools position="bottom-right" />
        </>
      )} */}
    </ErrorBoundary>
  );
}

/**
 * Root Error Boundary Component
 * 
 * Catches errors thrown by any route component and displays a fallback UI
 * Implements Requirements 14.1, 14.2
 */
function RootErrorComponent({ error }: { error: Error }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-destructive bg-card p-6 shadow-lg">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-destructive">{t('rootLayout:somethingWentWrong')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('rootLayout:errorOccurredWhileRendering')}
          </p>
        </div>
        
        <div className="rounded-md bg-destructive/10 p-4">
          <p className="text-sm font-mono text-destructive">{error.message}</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t('rootLayout:reloadPage')}
          </button>
          <button
            onClick={() => window.history.back()}
            className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            {t('rootLayout:goBack')}
          </button>
        </div>

        {import.meta.env.DEV && (
          <details className="mt-4">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              {t('rootLayout:errorDetailsDev')}
            </summary>
            <pre className="mt-2 overflow-auto rounded-md bg-muted p-4 text-xs">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
